import { add, listen, qs, remove, toggle } from '@fluorescent/dom';
import { getSizedImageUrl } from '@shopify/theme-images';
import Delegate from 'ftdomdelegate';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { createFocusTrap } from 'focus-trap';
import { renderUnitPrice } from '@/lib/unit-pricing';

import { on } from '@/glow/events';
import { cart, formatMoney } from '@/glow';
import dispatchCustomEvent from '@/lib/dispatch-custom-event';

const { strings } = window.theme;

import svg from '@/lib/svg';

export default function QuickCart(node) {
  const delegate = new Delegate(node);
  const overlay = qs('[data-overlay]', node);

  const cartTab = qs('[data-cart]', node);

  // Cart
  const itemsContainer = qs('[data-items]', node);
  const empty = qs('[data-empty]', node);
  const footer = qs('[data-footer]', node);
  const discounts = qs('[data-discounts]', footer);
  const subtotal = qs('[data-subtotal]', footer);
  const closeButton = qs('[data-close-icon]', node);

  delegate.on('click', 'button[data-decrease]', (_, target) => {
    const qty =
      parseInt(target.closest('.quick-cart__item').dataset.quantity) - 1;
    cart.updateItem(target.dataset.decrease, qty);
  });

  delegate.on('click', 'button[data-increase]', (_, target) => {
    const qty =
      parseInt(target.closest('.quick-cart__item').dataset.quantity) + 1;
    cart.updateItem(target.dataset.increase, qty);
  });

  delegate.on('click', 'button[data-remove]', (_, target) => {
    cart.updateItem(target.dataset.remove, 0);
  });

  const cartTrap = createFocusTrap(node, { allowOutsideClick: true });

  // Initial cart fetch
  cart.get().then(renderCart);

  // On every update
  on('cart:updated', ({ cart }) => {
    renderCart(cart);
    dispatchCustomEvent('cart:updated', { cart: cart });
  });

  const events = [
    listen([overlay, closeButton], 'click', close),
    listen(node, 'keydown', ({ keyCode }) => {
      if (keyCode === 27) close();
    }),
  ];

  on('cart:open', (_, { flash }) => open(flash));

  function open() {
    add(node, 'active');
    cartTrap.activate();
    setTimeout(() => {
      disableBodyScroll(node, {
        allowTouchMove: el => {
          while (el && el !== document.body) {
            if (el.getAttribute('data-scroll-lock-ignore') !== null) {
              return true;
            }
            el = el.parentNode;
          }
        },
        reserveScrollBarGap: true,
      });
      add(node, 'visible');
      add(cartTab, 'visible');
      cart
        .get()
        .then(cart => dispatchCustomEvent('quick-cart:open', { cart: cart }));
    }, 50);
  }

  function close() {
    remove(node, 'visible');
    setTimeout(() => {
      remove(node, 'active');
      enableBodyScroll(node);

      add(cartTab, 'visible');

      cartTrap.deactivate();
      dispatchCustomEvent('quick-cart:close');
    }, 350);
  }

  function renderCart(cart) {
    const { cart_level_discount_applications: cartDiscounts } = cart;
    itemsContainer.innerHTML = renderItems(cart);
    discounts.innerHTML = renderCartDiscounts(cartDiscounts);

    toggle(footer, 'visible', cart.sorted.length);
    toggle(empty, 'visible', !cart.sorted.length);
    toggle(discounts, 'visible', cartDiscounts.length);

    subtotal.innerHTML = formatMoney(cart.total_price);
  }

  function renderItems({ sorted }) {
    return r(
      sorted.length > 0,
      sorted.reduce((markup, item) => (markup += createItem(item)), '')
    );
  }

  function destroy() {
    events.forEach(unsubscribe => unsubscribe());
  }

  return { open, close, destroy };
}

function createItem({ line_level_discount_allocations: discounts, ...item }) {
  const imgSrc = item.featured_image ? item.featured_image.url : item.image;
  const imgAlt = item.featured_image ? item.featured_image.alt : '';
  const imgUrl = imgSrc && getSizedImageUrl(imgSrc, '240x');
  const image = r(
    imgSrc,
    `<img class="image__img lazyload" alt="${imgAlt}" data-src="${imgUrl}" />`
  );

  const sellingPlanName = item.selling_plan_allocation
    ? `<p class="fs-body-base c-subdued">${item.selling_plan_allocation.selling_plan.name}</p>`
    : ``;

  return `
    <div class="quick-cart__item ff-body fs-body-base" data-id="${
      item.variant_id
    }" data-quantity="${item.quantity}">
      <div class="quick-cart__item-left">
        <a href="${item.url}">
          <div class="quick-cart__image">${image}</div>
        </a>
      </div>
      <div class="quick-cart__item-middle">
        <h4 class="fs-body-bold"><a href="${item.url}">${
    item.product_title
  }</a></h4>
        <div>
          ${r(
            item.original_price > item.final_price,
            `<s class="qty">${formatMoney(item.original_price)}</s>`
          )}
          ${formatMoney(item.final_price)}
          ${r(
            item.quantity > 1,
            `<span class="c-subdued">x ${item.quantity}</span>`
          )}
        </div>
        ${renderUnitPrice(item.unit_price, item.unit_price_measurement)}
        ${renderOptions(item)}
        ${renderLineProperties(item.properties)}
        ${renderLineDiscounts(discounts)}
        ${sellingPlanName}
      </div>
      <div class="quick-cart__control">
        <div class="quick-cart__control-top">
          <button class="quick-cart__button quick-cart__button-increase" data-increase="${
            item.variant_id
          }" aria-label="${strings.quickCart.addProductQuantity}">
            ${svg.add}
          </button>
          <button class="quick-cart__button quick-cart__button-decrease" data-decrease="${
            item.variant_id
          }" aria-label="${strings.quickCart.removeProductQuantity}">
            ${svg.minus}
          </button>
          <button class="quick-cart__button quick-cart__button-remove" data-remove="${
            item.variant_id
          }" aria-label="${strings.quickCart.removeProduct}">
            ${svg.remove}
          </button>
        </div>
      </div>
    </div>
  `;
}

function renderOptions({ options_with_values: options, variant_title }) {
  return r(
    options.length > 0 && variant_title,
    options.reduce(
      (markup, { name, value }) => markup + `<div>${name}: ${value}</div>`,
      ''
    )
  );
}

function renderLineProperties(properties) {
  properties = properties || {}
  return r(
    Boolean(Object.keys(properties).length),
    Object.keys(properties).reduce(
      (markup, title) => markup + `<div class="quick-cart__item-properties">${properties[title] ? `${title}: ` : ``}${properties[title]}</div>`,
      ''
    )
  );
}

function renderCartDiscounts(discounts) {
  return r(
    Boolean(discounts.length),
    `
      <ul>
        ${discounts.map(
          ({ title, total_allocated_amount: value }) =>
            `<div>${title} (-${formatMoney(value)})</div>`
        )}
      </ul>
    `
  );
}

function renderLineDiscounts(discounts) {
  const formatted_discounts = discounts.map(
    ({ amount, discount_application: { title } }) => {
      return `<li>${title} (-${formatMoney(amount)})</li>`;
    }
  );

  return r(
    Boolean(discounts.length),
    `<ul class="quick-cart__item-discounts fs-body-small fs-body-base-ns c-subdued">${formatted_discounts}</ul>`
  );
}

function r(bool, whenTrue) {
  return bool ? whenTrue : ``;
}
